import React from 'react';
import PropTypes from 'prop-types';
import '../styles/ErrorBoundary.css';

/**
 * Main Error Boundary component to catch JavaScript errors anywhere in the component tree
 *
 * Since React error boundaries must be class components (functional components cannot catch errors),
 * this is the only exception to our functional component standard.
 *
 * Features:
 * - Graceful error handling with user-friendly UI
 * - Error reporting for debugging
 * - Fallback UI customization
 * - Development vs production error display
 * - Automatic error recovery options
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      eventId: null
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error: error
    };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details for debugging
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    this.setState({
      error: error,
      errorInfo: errorInfo,
      eventId: this.generateEventId()
    });

    // Report error to monitoring service (if available)
    this.reportError(error, errorInfo);
  }

  generateEventId = () => {
    return `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  reportError = (error, errorInfo) => {
    // In production, you might want to send this to an error tracking service
    // like Sentry, LogRocket, or your own logging service
    if (process.env.NODE_ENV === 'production') {
      // Example: Sentry.captureException(error, { contexts: { react: errorInfo } });
      console.error('Production error reported:', {
        error: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        eventId: this.state.eventId
      });
    }
  }

  handleReload = () => {
    window.location.reload();
  }

  handleGoHome = () => {
    window.location.href = '/';
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      eventId: null
    });
  }

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.state.errorInfo, this.handleRetry);
      }

      // Default fallback UI
      return (
        <div className="error-boundary">
          <div className="error-boundary-container">
            <div className="error-boundary-content">
              <div className="error-boundary-icon">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" stroke="#dc3545" strokeWidth="2"/>
                  <line x1="15" y1="9" x2="9" y2="15" stroke="#dc3545" strokeWidth="2"/>
                  <line x1="9" y1="9" x2="15" y2="15" stroke="#dc3545" strokeWidth="2"/>
                </svg>
              </div>

              <h2 className="error-boundary-title">
                {this.props.title || 'Something went wrong'}
              </h2>

              <p className="error-boundary-message">
                {this.props.message || 'We encountered an unexpected error. Our team has been notified and is working on a fix.'}
              </p>

              {/* Show error details in development */}
              {process.env.NODE_ENV === 'development' && this.state.error && (
                <details className="error-boundary-details">
                  <summary>Error Details (Development)</summary>
                  <div className="error-boundary-error-info">
                    <h4>Error Message:</h4>
                    <pre>{this.state.error.message}</pre>

                    <h4>Stack Trace:</h4>
                    <pre>{this.state.error.stack}</pre>

                    {this.state.errorInfo && (
                      <>
                        <h4>Component Stack:</h4>
                        <pre>{this.state.errorInfo.componentStack}</pre>
                      </>
                    )}
                  </div>
                </details>
              )}

              {/* Error ID for support */}
              {this.state.eventId && (
                <p className="error-boundary-event-id">
                  Error ID: <code>{this.state.eventId}</code>
                </p>
              )}

              <div className="error-boundary-actions">
                <button
                  className="btn btn-primary"
                  onClick={this.handleRetry}
                  aria-label="Try again"
                >
                  Try Again
                </button>

                <button
                  className="btn btn-outline-secondary"
                  onClick={this.handleGoHome}
                  aria-label="Go to home page"
                >
                  Go Home
                </button>

                <button
                  className="btn btn-outline-secondary"
                  onClick={this.handleReload}
                  aria-label="Reload page"
                >
                  Reload Page
                </button>
              </div>

              {/* Support contact info */}
              <div className="error-boundary-support">
                <p>
                  If this problem persists, please contact support at{' '}
                  <a href="mailto:support@clockwisecpa.app">support@clockwisecpa.app</a>
                  {this.state.eventId && ` and include error ID: ${this.state.eventId}`}
                </p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  fallback: PropTypes.func,
  title: PropTypes.string,
  message: PropTypes.string,
};

export default ErrorBoundary;